const stripePublicKey = import.meta.env.VITE_STRIPE_PUB_KEY;

// One Stripe.js instance for the whole page: Elements created by one
// instance cannot be confirmed by another.
let stripeInstance = null;
function getStripe() {
  if (!stripeInstance) {
    stripeInstance = Stripe(stripePublicKey);
  }
  return stripeInstance;
}

// ---------------------------------------------------------------------------
// "Add new card" modal: saves a card to the customer via a SetupIntent.
// ---------------------------------------------------------------------------
const cardForm = document.getElementById("card-form");
if (cardForm) {
  setupForm(cardForm);
}

async function setupForm(paymentForm) {
  const stripe = getStripe();
  const paymentEl = document.getElementById("payment-element");
  const clientSecret = paymentEl.dataset.secret;
  const elements = stripe.elements({ clientSecret });
  const paymentElement = elements.create("payment");
  paymentElement.mount("#payment-element");
  const returnUrl = paymentForm.dataset.return;
  const submitButton = document.getElementById("add-card-submit");

  paymentForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    submitButton.disabled = true;
    submitButton.innerText = "Processing";
    const { error } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: returnUrl,
      },
    });

    if (error.type === "card_error" || error.type === "validation_error") {
      alert(error.message);
    } else {
      alert("An unexpected error occurred.");
    }
    submitButton.disabled = false;
    submitButton.innerText = "Add Card";
  });
}

// ---------------------------------------------------------------------------
// Guest step: email, plus an inline six-digit code when the email already
// has an account. Alpine component `guestCheckout` owns the UI state; this
// code posts the form and tells Alpine what came back.
// ---------------------------------------------------------------------------
const guestForm = document.getElementById("guest-form");

// A Turnstile token is single use; after each guest-start the widget must
// issue a new one or the next submit (including "Send a new code") fails.
function resetTurnstile() {
  if (window.turnstile && document.querySelector(".cf-turnstile")) {
    try {
      window.turnstile.reset();
    } catch (e) {
      // Widget not rendered yet; nothing to reset.
    }
  }
}

window.addEventListener("guest-resend", async (event) => {
  // Post the whole form so the Turnstile response (when configured) goes
  // along; guest-start ignores the code field.
  const body = new FormData(guestForm);
  body.set("email", event.detail);
  body.delete("code");
  try {
    await postPurchase("/actions/pys/purchase/guest-start", body);
    window.dispatchEvent(new CustomEvent("guest-verify", { detail: event.detail }));
  } catch (error) {
    failPurchase(error.message || "Could not send a new code. Please try again.");
  } finally {
    resetTurnstile();
  }
});

if (guestForm) {
  guestForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    const mode = guestForm.dataset.mode === "verify" ? "guest-verify" : "guest-start";
    window.dispatchEvent(new CustomEvent("purchase-submitting"));

    try {
      const result = await postPurchase("/actions/pys/purchase/" + mode, new FormData(guestForm));
      if (mode === "guest-start") {
        resetTurnstile();
      }
      if (result.requiresVerification) {
        window.dispatchEvent(new CustomEvent("guest-verify", { detail: result.email }));
        return;
      }
      if (result.redirect) {
        window.location.assign(result.redirect);
        return;
      }
      throw new Error(result.message || "Something went wrong. Please try again.");
    } catch (error) {
      if (mode === "guest-start") {
        resetTurnstile();
      }
      failPurchase(error.message || "Something went wrong. Please try again.");
    }
  });
}

// ---------------------------------------------------------------------------
// Purchase form: submits as JSON so the server can hand back a 3D Secure
// challenge (requiresAction) instead of failing the charge outright.
// Guests enter a card inline; it is saved to their Stripe customer first,
// then the purchase is charged against it like a saved card.
// ---------------------------------------------------------------------------
const paymentForm = document.getElementById("payment-form");
let guestElements = null;
let guestPaymentElement = null;

function mountGuestCard(clientSecret) {
  if (guestPaymentElement) {
    guestPaymentElement.destroy();
  }
  guestElements = getStripe().elements({ clientSecret });
  guestPaymentElement = guestElements.create("payment");
  guestPaymentElement.mount("#guest-payment-element");
}

// The purchase failed after the guest's card was saved. That SetupIntent is
// spent and the saved method may be the problem, so forget it and mount a
// fresh card form against a new SetupIntent.
async function resetGuestCard() {
  const cardIdInput = document.getElementById("guest-card-id");
  if (!guestElements || !cardIdInput || !cardIdInput.value) {
    return;
  }
  cardIdInput.value = "";
  try {
    const data = await postPurchase("/actions/pys/purchase/guest-setup-intent", new FormData());
    if (data.clientSecret) {
      mountGuestCard(data.clientSecret);
    }
  } catch (e) {
    // Leave the form as is; reloading the page issues a new SetupIntent.
  }
}

if (paymentForm) {
  resumeAfterPaymentRedirect();

  const guestPaymentEl = document.getElementById("guest-payment-element");

  if (guestPaymentEl && guestPaymentEl.dataset.secret) {
    mountGuestCard(guestPaymentEl.dataset.secret);
    resumeAfterSetupRedirect(getStripe());
  }

  paymentForm.addEventListener("change", async () => {
    const formData = new FormData(paymentForm);
    await fetch("/actions/pys/purchase/session", {
      method: "POST",
      body: formData,
    });
  });

  paymentForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    window.dispatchEvent(new CustomEvent("purchase-submitting"));

    try {
      if (guestElements) {
        await saveGuestCard(guestElements);
      }
      const result = await postPurchase("/actions/pys/purchase", new FormData(paymentForm));
      await handlePurchaseResult(result);
    } catch (error) {
      failPurchase(error.message || "Something went wrong. Please try again.");
      await resetGuestCard();
    }
  });
}

async function saveGuestCard(elements) {
  const cardIdInput = document.getElementById("guest-card-id");
  if (cardIdInput.value) {
    return;
  }

  const stripe = getStripe();
  const returnUrl = document.getElementById("guest-payment-element").dataset.return;
  const nameInput = document.getElementById("guest-card-name");
  const name = nameInput ? nameInput.value.trim() : "";
  if (nameInput && !name) {
    nameInput.focus();
    throw new Error("Please enter the name on your card.");
  }

  const { error, setupIntent } = await stripe.confirmSetup({
    elements,
    redirect: "if_required",
    confirmParams: {
      return_url: returnUrl,
      payment_method_data: { billing_details: { name } },
    },
  });

  if (error) {
    throw new Error(error.message || "We couldn't save your card. Please check the details and try again.");
  }

  cardIdInput.value = setupIntent.payment_method;
}

// A hosted 3D Secure page sent the student back here with the PaymentIntent
// in the URL. Finish the purchase from it (the server verifies its status).
async function resumeAfterPaymentRedirect() {
  const params = new URLSearchParams(window.location.search);
  const intentId = params.get("payment_intent");
  if (!intentId) {
    return false;
  }

  window.dispatchEvent(new CustomEvent("purchase-submitting"));
  try {
    const body = new FormData();
    body.append("payment_intent_id", intentId);
    const completion = await postPurchase("/actions/pys/purchase/complete", body);
    await handlePurchaseResult(completion);
  } catch (error) {
    failPurchase(error.message || "Something went wrong. Please try again.");
  }
  return true;
}

// A redirect-based payment method bounced us back here with the SetupIntent
// in the URL. Pick the saved card up and finish the purchase.
async function resumeAfterSetupRedirect(stripe) {
  const params = new URLSearchParams(window.location.search);
  const secret = params.get("setup_intent_client_secret");
  if (!secret) {
    return;
  }

  const { setupIntent } = await stripe.retrieveSetupIntent(secret);
  if (setupIntent && setupIntent.status === "succeeded" && setupIntent.payment_method) {
    document.getElementById("guest-card-id").value = setupIntent.payment_method;
    paymentForm.requestSubmit();
  }
}

async function postPurchase(url, body) {
  const response = await fetch(url, {
    method: "POST",
    headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
    body,
  });

  let data = {};
  try {
    data = await response.json();
  } catch (e) {
    data = {};
  }

  if (!response.ok && !data.requiresAction) {
    throw new Error(data.message || "Your purchase could not be completed.");
  }

  return data;
}

async function handlePurchaseResult(result) {
  if (result.requiresAction && result.clientSecret) {
    window.dispatchEvent(new CustomEvent("purchase-authenticating"));

    const stripe = getStripe();
    const { error, paymentIntent } = await stripe.handleNextAction({
      clientSecret: result.clientSecret,
    });

    if (error) {
      throw new Error(error.message || "Card authentication failed. Please try another card.");
    }

    const body = new FormData();
    body.append("payment_intent_id", paymentIntent ? paymentIntent.id : result.paymentIntentId);

    const completion = await postPurchase("/actions/pys/purchase/complete", body);
    return handlePurchaseResult(completion);
  }

  if (result.redirect) {
    window.location.assign(result.redirect);
    return;
  }

  throw new Error(result.message || "Your purchase could not be completed.");
}

function failPurchase(message) {
  window.dispatchEvent(new CustomEvent("purchase-error", { detail: message }));
}
